import {
  Banner,
  Box,
  Button,
  Input,
  Typography,
} from '@oresundsbron/bridge-ui';
import {
  FormStoreProvider,
  useForm,
  useFormParams,
} from '@oresundsbron/use-form';
import { FC, ReactNode, useCallback, useState } from 'react';
import { AlprForm, AlprProps } from './BusinessAlpr';
import { FieldSet } from '../Fieldset';
import { pipe } from 'fp-ts/lib/function';
import { Plus } from '@oresundsbron/icons';
import { getOrElse, map } from 'fp-ts/lib/Option';
import { FormButton } from '../Buttons';
import { Alpr, Data, Labels, Placeholders } from './types';
import { deleteAt } from 'fp-ts/lib/Array';
import { AlprEntry } from './AlprEntry';

export type { Alpr, Data } from './types';

const defaultValues: useFormParams<Data>['defaultValues'] = {
  paymentEntries: [],
};

export const BusinessRegisterAlpr: FC<
  {
    title: string;
    description: ReactNode;
    labels: Labels & { orderBizz: string; skipAlpr: string };
    countryOptions: AlprProps['countryOptions'];
    bvSupportCountries: AlprProps['bvSupportCountries'];
    fuelTypeOptions: AlprProps['fuelTypeOptions'];
    emissionClassOptions: AlprProps['emissionClassOptions'];
    weightClassOptions: AlprProps['weightClassOptions'];
    maxEntries: number;
    onAlprValidation: useFormParams<Alpr>['onValidate'];
    useOnOtherPlacesDisclaimer: string;
    placeholders: Placeholders;
    defaultCountry?: string;
    maxEntriesDisclaimer: ReactNode;
    skipAlprDisclaimer: ReactNode;
    commuter?: boolean;
    vehicleColors: AlprProps['vehicleColors'];
  } & useFormParams<Data>
> = ({
  title,
  namespace,
  description,
  labels,
  countryOptions,
  bvSupportCountries,
  weightClassOptions,
  emissionClassOptions,
  fuelTypeOptions,
  maxEntries,
  onAlprValidation,
  onValidate,
  useOnOtherPlacesDisclaimer,
  placeholders,
  defaultCountry,
  maxEntriesDisclaimer,
  commuter,
  skipAlprDisclaimer,
  vehicleColors,
}) => {
  const [skip, setSkip] = useState('');
  const { stepNr, getData, validate, isValid, isActive, transformData } =
    useForm<Data>({
      namespace,
      title,
      defaultValues,
      onValidate,
    });

  const entries = getData().paymentEntries || [];

  const [isEditing, toggleIsEditing] = useState(() => entries.length === 0);
  const [activeEntry, setActiveEntry] = useState<number>(0);

  const addEntry = useCallback(
    (data: Alpr) => {
      transformData((curr) => ({
        paymentEntries: pipe(curr.paymentEntries || [], (arr) => {
          arr.splice(activeEntry, 1, data);
          return arr;
        }),
      }));
      toggleIsEditing(false);
    },
    // eslint-disable-next-line
    [toggleIsEditing, activeEntry]
  );

  const remove = useCallback(
    (entry: number) =>
      transformData((curr) =>
        pipe(
          curr.paymentEntries,
          deleteAt(entry),
          map((updated) => ({ paymentEntries: updated })),
          getOrElse(() => curr),
          (updated) => {
            if (updated.paymentEntries.length === 0) {
              toggleIsEditing(true);
            }
            return updated;
          }
        )
      ),
    // eslint-disable-next-line
    [toggleIsEditing]
  );

  const addMore = useCallback(() => {
    setActiveEntry((curr) => curr + 1);
    toggleIsEditing(true);
  }, [setActiveEntry, toggleIsEditing]);

  return (
    <FieldSet nr={stepNr} title={title} id={namespace}>
      <Box className="col-span-full">{description}</Box>
      {entries.map((d, i) => {
        return (
          <AlprEntry
            key={i}
            labels={labels}
            {...d}
            onRemove={() => remove(i)}
            disabled={!isActive}
          />
        );
      })}
      {isEditing ? (
        <FormStoreProvider>
          <AlprForm
            onValidate={onAlprValidation}
            labels={labels}
            onCancel={() => toggleIsEditing(false)}
            countryOptions={countryOptions}
            bvSupportCountries={bvSupportCountries}
            weightClassOptions={weightClassOptions}
            emissionClassOptions={emissionClassOptions}
            fuelTypeOptions={fuelTypeOptions}
            onAdd={addEntry}
            displayCancel={entries.length > 0}
            useOnOtherPlacesDisclaimer={useOnOtherPlacesDisclaimer}
            placeholders={placeholders}
            disableForm={!isActive || skip === 'true'}
            vehicleColors={vehicleColors}
            isCommuter={commuter}
          />
        </FormStoreProvider>
      ) : null}
      {entries.length + 1 <= maxEntries && !isEditing ? (
        <Button
          startIcon={<Plus />}
          intent="text"
          onClick={addMore}
          disabled={!isActive}
        >
          {labels.addMore}
        </Button>
      ) : null}
      {entries.length >= maxEntries && !commuter ? (
        <Banner className="col-span-full">{maxEntriesDisclaimer}</Banner>
      ) : null}
      {!commuter ? (
        <>
          <Input
            className="col-span-full"
            type="checkbox"
            name="skip-entries"
            value="true"
            disabled={!isActive || entries.length > 0}
            checked={skip === 'true'}
            onChange={() =>
              setSkip((curr) => (curr === 'true' ? 'false' : 'true'))
            }
            label={labels.skipAlpr}
          />
          {skip === 'true' ? (
            <Banner className="col-span-full">
              <Typography size="sm">{skipAlprDisclaimer}</Typography>
            </Banner>
          ) : null}
        </>
      ) : null}
      <FormButton
        loadingLabel={labels.loading}
        namespace={namespace}
        onClick={validate}
        disabled={
          (!isActive || !isValid || !entries.length || isEditing) &&
          skip !== 'true'
        }
      />
    </FieldSet>
  );
};
